/**
 * CommissioningPage — multi-step wizard parallel to ServiceTechPage.
 *
 * Steps:
 *  1. Pick compressor (lists from /api/service-tech/fleet — same gating)
 *  2. Walkaround video
 *  3. Disconnect box + compressor wide picture
 *  4. Co-installed products (auto-pull from invoice; nameplate uploads
 *     when sold through a dealer)
 *  5. Auto-drains (label close-up + wide install context, repeatable)
 *  6. Voltage (3-phase OR 220V single-phase, gated by detected phase_count)
 *  7. Wire diameter (skip dryer wire when is_air_center)
 *  8. Line reactor (only when is_vsd)
 *  9. Warranty acceptance: role-aware doc, typed/drawn signature, PDF email.
 *
 * MOSTLY mirrors the ServiceTechPage UX but does NOT reuse its
 * component (different captures, different state machine, different rules).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Wrench, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import AccountMenu from "@/components/AccountMenu";
import LiveCameraCapture from "@/components/LiveCameraCapture";
import { MeasurementsSet, isMeasurementsSetComplete } from "@/components/MeasurementCapture";
import {
  groupMasterDocSections,
  policyGroupCounts,
} from "@/lib/masterDocGrouping";

const STEPS = [
  "serial", "nameplate", "video", "disconnect", "coinstalled", "drains",
  "voltage", "wire", "line_reactor", "disclaimer",
];

export default function CommissioningPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { visitId: routeVisitId } = useParams();
  // If we arrived from the history page, the back button returns there
  // instead of going home. Falls back to "/" for direct deep links.
  const backTarget = location.state?.from || "/";

  // Visit / session state
  const [visit, setVisit] = useState(null);            // server doc
  const [step, setStep] = useState("serial");

  // ─── Step 1: compressor pick ──────────────────────────────────
  const [fleet, setFleet] = useState([]);
  const [fleetSearch, setFleetSearch] = useState("");
  const [fleetLoading, setFleetLoading] = useState(false);

  // ─── Master document & disclaimer ─────────────────────────────
  const [masterDoc, setMasterDoc] = useState(null);
  const [signerName, setSignerName] = useState(user?.name || "");
  const [signatureMethod, setSignatureMethod] = useState("typed");
  const [signatureData, setSignatureData] = useState("");
  const [acceptedKeys, setAcceptedKeys] = useState({});
  const [signedResult, setSignedResult] = useState(null);

  // ─── Co-installed products lookup ─────────────────────────────
  const [coInstalled, setCoInstalled] = useState({ items: [], through_dealer: false, manual_upload_required: false });

  // ─── Nameplate OCR + sequence history ─────────────────────────
  const [snPreview, setSnPreview] = useState(null);  // {sn_match, extracted_sn, ...}
  const [nameplateUploaded, setNameplateUploaded] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [history, setHistory] = useState([]);  // by-serial prior commissionings

  // ─── Engineering-review send state ────────────────────────────
  const [sendingEng, setSendingEng] = useState(false);
  const [engResult, setEngResult] = useState(null);
  const [engNotes, setEngNotes] = useState("");

  // ─── Drain editing state (repeatable list) ────────────────────
  const [drains, setDrains] = useState([]);

  // ─── Voltage / wire fields ────────────────────────────────────
  const [voltage, setVoltage] = useState({ l1_l2: "", l2_l3: "", l1_l3: "" });
  // iter150AG — new photo-only measurement set. Items are loaded by
  // <MeasurementsSet> from /api/measurements; we keep a local copy here
  // only to drive the "Continue" gate.
  const [measItems, setMeasItems] = useState([]);
  const measComplete = isMeasurementsSetComplete(
    measItems, (visit?.phase_count === 1 ? 1 : 3), true);
  const [wireC, setWireC] = useState("");
  const [wireD, setWireD] = useState("");
  const [seqReason, setSeqReason] = useState("");

  // ─── Resume on refresh ────────────────────────────────────────
  useEffect(() => {
    if (!routeVisitId) return;
    // Skip if we already have this visit in state (e.g. just created via
    // startCommissioning() which navigated us here). Without this guard the
    // resume effect would clobber the nameplate step.
    if (visit?.id === routeVisitId) return;
    api.get(`/commissioning/${routeVisitId}`)
      .then(r => {
        setVisit(r.data);
        setDrains(r.data.autodrains || []);
        setVoltage(r.data.measurements?.voltage || { l1_l2: "", l2_l3: "", l1_l3: "" });
        setWireC(r.data.measurements?.wire_diameter_compressor || "");
        setWireD(r.data.measurements?.wire_diameter_dryer || "");
        setSeqReason(r.data.sequence_reason || "");
        // Pick a sensible step based on what's already captured:
        //   • submitted → disclaimer (showing PDF + send-to-eng)
        //   • nameplate uploaded → continue from video
        //   • otherwise → nameplate
        const status = r.data.status;
        const kinds = new Set(r.data.media_kinds_present || []);
        if (status === "submitted") setStep("disclaimer");
        else if (kinds.has("nameplate")) setStep("video");
        else setStep("nameplate");
      })
      .catch(() => toast.error("Could not load that commissioning record"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeVisitId]);

  // iter136.34 — When the user lands here from a "Open commissioning"
  // CTA (currently the warranty-claim ``commissioning_required`` toast),
  // the URL carries either ``?serial=<serial_number>`` (legacy) or
  // ``?serial_id=<id>`` (preferred). Auto-resolve to a fleet row and
  // start the commissioning so the user doesn't have to re-pick what
  // the system already knows.
  useEffect(() => {
    if (routeVisitId) return; // resume flow handles this
    if (visit) return;
    const qs = new URLSearchParams(location.search || "");
    const querySerialId = qs.get("serial_id") || "";
    const querySerial = qs.get("serial") || "";
    if (!querySerialId && !querySerial) return;

    let cancelled = false;
    (async () => {
      try {
        let serialId = querySerialId;
        if (!serialId && querySerial) {
          // Resolve the serial-number string to a serial_id by hitting
          // the same fleet endpoint. ``q`` does an ILIKE match on the
          // serial number; we accept the first exact match.
          const r = await api.get("/service-tech/fleet", { params: { q: querySerial, limit: 50 } });
          const items = r.data?.items || [];
          const exact = items.find((x) => String(x.serial_number || "").toUpperCase() === querySerial.toUpperCase());
          serialId = (exact || items[0])?.serial_id || "";
        }
        if (!serialId || cancelled) return;
        await startCommissioning(serialId);
      } catch (e) {
        // Surface but don't block — fall back to the normal pick step.
        toast.error("Could not auto-open commissioning for that compressor — pick it from the list.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, routeVisitId]);

  // ─── Pick step: load fleet ────────────────────────────────────
  useEffect(() => {
    if (step !== "serial" || visit) return;
    setFleetLoading(true);
    api.get("/service-tech/fleet", { params: { q: fleetSearch, limit: 200 } })
      .then(r => setFleet(r.data?.items || []))
      .catch(() => toast.error("Could not load fleet"))
      .finally(() => setFleetLoading(false));
  }, [step, fleetSearch, visit]);

  // ─── Disclaimer step: load master doc ─────────────────────────
  useEffect(() => {
    if (step !== "disclaimer" || masterDoc) return;
    api.get("/commissioning/master-doc")
      .then(r => {
        setMasterDoc(r.data);
        // Prefill all signature clauses as accepted (user can untick).
        const m = {};
        (r.data.signature_clauses || []).forEach(c => { m[c.key] = true; });
        setAcceptedKeys(m);
      })
      .catch(() => toast.error("Could not load warranty document"));
  }, [step, masterDoc]);

  // ─── Co-installed lookup when entering that step ──────────────
  useEffect(() => {
    if (step !== "coinstalled" || !visit) return;
    api.get(`/commissioning/${visit.id}/co-installed`)
      .then(r => setCoInstalled(r.data))
      .catch(() => {/* nonfatal */});
  }, [step, visit]);

  const startCommissioning = async (serial_id) => {
    try {
      const r = await api.post("/commissioning/start", { serial_id });
      setVisit(r.data);
      // Load past commissionings to show as historical context.
      try {
        const h = await api.get(`/commissioning/by-serial/${serial_id}`);
        setHistory((h.data?.items || []).filter(v => v.status === "submitted"));
      } catch (e) {/* nonfatal */}
      setStep("nameplate");
      navigate(`/commissioning/${r.data.id}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start commissioning");
    }
  };

  const previewSn = async () => {
    if (!visit) return;
    setPreviewing(true);
    try {
      const r = await api.post(`/commissioning/${visit.id}/preview-sn`);
      setSnPreview(r.data);
      if (r.data.sn_match) {
        toast.success(`SN matched: ${r.data.extracted_sn}`);
      } else {
        toast.warning(`SN mismatch — nameplate reads ${r.data.extracted_sn || "(unknown)"} vs expected ${r.data.expected_sn}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "OCR failed");
    } finally {
      setPreviewing(false);
    }
  };

  const sendToEngineering = async () => {
    if (!visit) return;
    setSendingEng(true);
    try {
      const r = await api.post(`/commissioning/${visit.id}/send-to-engineering`,
                               { notes: engNotes });
      setEngResult(r.data);
      toast.success("Sent to ACC engineering for review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingEng(false);
    }
  };

  const uploadMedia = async (file, kind, subkey = "") => {
    if (!file || !visit) return null;
    const fd = new FormData();
    fd.append("upload", file);
    fd.append("kind", kind);
    fd.append("subkey", subkey);
    try {
      const r = await api.post(`/commissioning/${visit.id}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind} uploaded`);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind} upload failed`);
      return null;
    }
  };

  const saveMeasurements = async (overrides = {}) => {
    if (!visit) return;
    const payload = {
      voltage: {
        l1_l2: voltage.l1_l2 ? Number(voltage.l1_l2) : null,
        l2_l3: voltage.l2_l3 ? Number(voltage.l2_l3) : null,
        l1_l3: voltage.l1_l3 ? Number(voltage.l1_l3) : null,
      },
      wire_diameter_compressor: wireC || null,
      wire_diameter_dryer: visit.is_air_center ? null : (wireD || null),
      autodrains: drains,
      co_installed_products: coInstalled.items.map(p => ({
        product_name: p.product_name || "",
        computed_key: p.computed_key || "",
        qty: p.qty || 1,
        source: p.source || "invoice",
        invoice_heading_id: p.invoice_heading_id,
        nameplate_picture_id: p.nameplate_picture_id || null,
      })),
      sequence_reason: seqReason || null,
      ...overrides,
    };
    try {
      await api.patch(`/commissioning/${visit.id}/measurements`, payload);
      // Refresh visit doc.
      const r = await api.get(`/commissioning/${visit.id}`);
      setVisit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const sign = async () => {
    if (!visit || !signerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (signatureMethod === "drawn" && !signatureData) {
      toast.error("Please draw your signature");
      return;
    }
    try {
      const r = await api.post(`/commissioning/${visit.id}/sign-disclaimer`, {
        signer_name: signerName,
        signature_method: signatureMethod,
        signature_data: signatureData,
        accepted: true,
        accepted_clause_keys: Object.keys(acceptedKeys).filter(k => acceptedKeys[k]),
      });
      setSignedResult(r.data);
      toast.success(t("commissioning.signed_success"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    }
  };

  const stepIndex = STEPS.indexOf(step);
  const goNext = () => {
    let next = stepIndex + 1;
    // Dynamic skip logic
    if (visit) {
      if (STEPS[next] === "line_reactor" && !visit.is_vsd) next++;
    }
    setStep(STEPS[next] || "disclaimer");
  };
  const goBack = () => setStep(STEPS[Math.max(0, stepIndex - 1)] || "serial");

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-5xl mx-auto">
        <button data-testid="commissioning-back-home" onClick={() => navigate(backTarget)}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title={backTarget === "/commissioning/history" ? "Back to history" : "Home"}>
          <ArrowLeft size={18} weight="bold" />
        </button>
        <Wrench size={20} weight="fill" className="text-emerald-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']">{t("commissioning.page_header_short")}</h1>
        <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6">
        <header className="mb-6" data-testid="commissioning-header">
          <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900">
            {t("commissioning.page_header_full")}
          </h1>
          <p className="text-sm text-slate-600 mt-1">{t("commissioning.subtitle")}</p>
          {visit && (
            <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
              <Pill data-testid="commissioning-serial-pill">SN {visit.serial_number}</Pill>
              <Pill>{visit.product_name}</Pill>
              <Pill data-testid="commissioning-seq-pill" tone="violet">
                #{visit.sequence_number} {t(visit.sequence_number === 1
                  ? "commissioning.sequence_first"
                  : "commissioning.sequence_nth", null,
                  { n: ordinal(visit.sequence_number) })}
              </Pill>
              {visit.is_vsd && <Pill tone="amber">VSD</Pill>}
              {visit.is_air_center && <Pill tone="blue">{t("commissioning.air_center", "Air Center")}</Pill>}
              <Pill tone="slate">{visit.phase_count}-phase</Pill>
              {visit.engineering_review_status && (
                <Pill
                  data-testid="commissioning-eng-status-badge"
                  tone={
                    visit.engineering_review_status === "approved" ? "emerald" :
                    visit.engineering_review_status === "rejected" ? "red" :
                    visit.engineering_review_status === "needs_changes" ? "orange" : "amber"
                  }>
                  Eng: {String(visit.engineering_review_status).replace("_", " ")}
                </Pill>
              )}
            </div>
          )}
        </header>

        <Stepper steps={STEPS} current={step} skipLineReactor={visit && !visit.is_vsd} />

        {/* Step 1: Pick a compressor */}
        {step === "serial" && !visit && (
          <Card data-testid="commissioning-step-serial">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.serial_step")}</h2>
            <p className="text-sm text-slate-600 mb-4">{t("commissioning.serial_pick")}</p>
            <input
              data-testid="commissioning-fleet-search"
              type="search"
              placeholder={t("commissioning.fleet_search_placeholder", "Search by serial number, model, or company")}
              value={fleetSearch}
              onChange={e => setFleetSearch(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-3"
            />
            <div className="border rounded divide-y max-h-96 overflow-y-auto">
              {fleetLoading && <div className="p-4 text-sm text-slate-500">Loading…</div>}
              {!fleetLoading && fleet.length === 0 && (
                <div className="p-4 text-sm text-slate-500">{t("commissioning.no_compressors_match", "No compressors match.")}</div>
              )}
              {fleet.map(c => (
                <button
                  key={c.serial_id}
                  data-testid={`commissioning-fleet-row-${c.serial_id}`}
                  onClick={() => startCommissioning(c.serial_id)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-3"
                >
                  <div>
                    <div className="font-mono text-sm font-bold">{c.serial_number || "(no SN)"}</div>
                    <div className="text-xs text-slate-600">{c.product_name}</div>
                  </div>
                  <div className="text-xs text-slate-500 truncate max-w-[200px]">{c.company_name}</div>
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Step 2: Nameplate OCR */}
        {step === "nameplate" && visit && (
          <Card data-testid="commissioning-step-nameplate">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.nameplate_title", "Nameplate photo + SN match")}</h2>
            <p className="text-sm text-slate-600 mb-3">
              {t("commissioning.nameplate_intro", "Take a clear photo of the compressor nameplate. The AI will read the serial number and confirm it matches")} <span className="font-mono font-bold">{visit.serial_number}</span>.
            </p>
            <StepHint stepKey="nameplate" t={t} />
            <FileUploader kind="nameplate" accept="image/*"
              onUpload={async (f) => {
                const m = await uploadMedia(f, "nameplate");
                if (m) setNameplateUploaded(true);
                return m;
              }} />
            {nameplateUploaded && !snPreview && (
              <button data-testid="commissioning-preview-sn" onClick={previewSn} disabled={previewing}
                className="mt-3 px-4 py-2 bg-blue-600 text-white rounded font-bold text-sm hover:bg-blue-700 disabled:opacity-50">
                {previewing ? "Reading…" : "Run AI OCR + check SN match"}
              </button>
            )}
            {snPreview && (
              <div data-testid="commissioning-sn-preview"
                className={`mt-3 border-l-4 p-3 rounded ${snPreview.sn_match ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"}`}>
                <div className="text-sm font-bold">
                  {snPreview.sn_match ? "✓ SN matched" : "⚠ SN mismatch"}
                </div>
                <div className="text-xs text-slate-700 mt-1">
                  {t("commissioning.nameplate_reads", "Nameplate reads")} <code>{snPreview.extracted_sn || "(unreadable)"}</code><br />
                  Expected from compressor record: <code>{snPreview.expected_sn}</code><br />
                  Confidence: {(snPreview.confidence * 100).toFixed(0)}%
                </div>
                {!snPreview.sn_match && (
                  <p className="text-xs text-amber-800 mt-1">
                    {t("commissioning.wrong_unit_hint", "If this is the wrong unit, please cancel and re-pick the compressor.")}
                  </p>
                )}
              </div>
            )}
            {/* Past commissionings (historical context) */}
            {history.length > 0 && (
              <div className="mt-4 border-t pt-3">
                <h3 className="text-sm font-bold mb-2">{t("commissioning.past_on_unit", "Past commissionings on this unit")} ({history.length})</h3>
                <ul className="text-xs space-y-1">
                  {history.map(h => (
                    <li key={h.id} className="flex items-center gap-2">
                      <span className="bg-slate-100 px-2 py-0.5 rounded font-bold">#{h.sequence_number}</span>
                      <span>{h.finished_at ? new Date(h.finished_at).toLocaleDateString() : "draft"}</span>
                      <span className="text-slate-500">by {h.tech_email}</span>
                      {h.disclaimer?.signed_pdf_filename && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/commissioning/${h.id}/disclaimer.pdf`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-bold ml-auto">PDF →</a>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-slate-500 mt-2 italic">
                  {t("commissioning.past_help", "Previous records help diagnose what changed (environment, ownership, install) since the prior commissioning.")}
                </p>
              </div>
            )}
            <NavRow visit={visit} step={step} onBack={goBack} onNext={goNext} t={t} />
          </Card>
        )}

        {/* Step 3: Walkaround video */}
        {step === "video" && visit && (
          <Card data-testid="commissioning-step-video">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.video_step")}</h2>
            <p className="text-sm text-slate-600 mb-3">{t("commissioning.video_hint")}</p>
            <StepHint stepKey="video" t={t} />
            <FileUploader kind="video" accept="video/*" onUpload={(f) => uploadMedia(f, "video")} />
            <NavRow visit={visit} step={step} onBack={goBack} onNext={goNext} t={t} skipLabel={t("commissioning.action_skip")} />
            {visit.sequence_number > 1 && (
              <div className="mt-4 p-3 border-l-4 border-amber-400 bg-amber-50">
                <label className="block text-sm font-bold mb-1">{t("commissioning.sequence_reason")}</label>
                <p className="text-xs text-slate-600 mb-2">{t("commissioning.sequence_reason_hint")}</p>
                <textarea
                  data-testid="commissioning-sequence-reason"
                  value={seqReason}
                  onChange={e => setSeqReason(e.target.value)}
                  rows={3}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder={t("commissioning.sequence_reason_placeholder", "e.g. Moved from previous shop, restarted after 18 months idle…")}
                />
              </div>
            )}
          </Card>
        )}

        {/* Step 3: Disconnect + main compressor */}
        {step === "disconnect" && visit && (
          <Card data-testid="commissioning-step-disconnect">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.disconnect_step")}</h2>
            <p className="text-sm text-slate-600 mb-3">{t("commissioning.disconnect_hint")}</p>
            <StepHint stepKey="disconnect" t={t} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FileUploader kind="disconnect_box" label={t("commissioning.disconnect_box", "Disconnect / electrical box")} accept="image/*" onUpload={(f) => uploadMedia(f, "disconnect_box")} />
              <FileUploader kind="compressor" label={t("commissioning.compressor_wide", "Main compressor — wide picture")} accept="image/*" onUpload={(f) => uploadMedia(f, "compressor")} />
            </div>
            <NavRow visit={visit} step={step} onBack={goBack} onNext={goNext} t={t} />
          </Card>
        )}

        {/* Step 4: Co-installed products */}
        {step === "coinstalled" && visit && (
          <Card data-testid="commissioning-step-coinstalled">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.coinstalled_step")}</h2>
            <StepHint stepKey="coinstalled" t={t} />
            {coInstalled.items.length > 0 && (
              <>
                <p className="text-sm text-slate-600 mb-2">{t("commissioning.coinstalled_invoice_found")}</p>
                <ul data-testid="commissioning-coinstalled-list" className="border rounded divide-y mb-3">
                  {coInstalled.items.map((p, i) => (
                    <li key={i} className="px-3 py-2 text-sm flex justify-between gap-3">
                      <span>{p.product_name}</span>
                      <span className="text-xs text-slate-500">qty {p.qty} {p.product_class && `· ${p.product_class}`}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {coInstalled.manual_upload_required && (
              <div className="p-3 border-l-4 border-orange-400 bg-orange-50 mb-3">
                <p className="text-sm">{t("commissioning.coinstalled_through_dealer")}</p>
                <FileUploader kind="coinstalled_nameplate" label={t("commissioning.coinstalled_nameplate_label", "Nameplate of co-installed product")} accept="image/*" multiple onUpload={(f, idx) => uploadMedia(f, "coinstalled_nameplate", `n_${idx}`)} />
              </div>
            )}
            <NavRow visit={visit} step={step} onBack={goBack} onNext={goNext} t={t} />
          </Card>
        )}

        {/* Step 5: Auto-drains */}
        {step === "drains" && visit && (
          <Card data-testid="commissioning-step-drains">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.autodrains_step")}</h2>
            <p className="text-sm text-slate-600 mb-3">{t("commissioning.autodrains_hint")}</p>
            <StepHint stepKey="drains" t={t} />
            <div className="space-y-3">
              {drains.map((d, i) => (
                <div key={i} className="border rounded p-3 bg-white">
                  <input
                    data-testid={`commissioning-drain-label-${i}`}
                    type="text"
                    placeholder={`Drain #${i + 1} label`}
                    value={d.label || ""}
                    onChange={e => {
                      const c = [...drains]; c[i] = { ...c[i], label: e.target.value }; setDrains(c);
                    }}
                    className="w-full border rounded px-2 py-1 text-sm mb-2"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <FileUploader kind="drain_label" label={t("commissioning.upload_label")} accept="image/*"
                      onUpload={async (f) => {
                        const m = await uploadMedia(f, "drain_label", `d_${i}`);
                        if (m) { const c = [...drains]; c[i] = { ...c[i], label_picture_id: m.id }; setDrains(c); }
                      }} />
                    <FileUploader kind="drain_context" label={t("commissioning.upload_context")} accept="image/*"
                      onUpload={async (f) => {
                        const m = await uploadMedia(f, "drain_context", `d_${i}`);
                        if (m) { const c = [...drains]; c[i] = { ...c[i], context_picture_id: m.id }; setDrains(c); }
                      }} />
                  </div>
                </div>
              ))}
              <button data-testid="commissioning-add-drain"
                onClick={() => setDrains([...drains, { label: "", label_picture_id: null, context_picture_id: null }])}
                className="text-sm font-bold text-violet-700 hover:text-violet-900">
                + {t("commissioning.add_drain")}
              </button>
            </div>
            <NavRow visit={visit} step={step}
              onBack={goBack}
              onNext={async () => { await saveMeasurements(); goNext(); }}
              t={t} />
          </Card>
        )}

        {/* Step 6: Voltage + Amps measurements (photo-only, AI-read).
            iter150AG — replaces the legacy typed-number step. Existing
            visits that pre-date this change still show their old
            typed-in values via the legacy panel for grandfathered audit.
            Detect legacy mode by the presence of measurements.voltage.l1_l2. */}
        {step === "voltage" && visit && (
          <Card data-testid="commissioning-step-voltage">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.voltage_step")}</h2>
            <p className="text-sm text-slate-600 mb-3">
              {visit.phase_count === 1 ? t("commissioning.voltage_hint_1p") : t("commissioning.voltage_hint_3p")}
            </p>
            <StepHint stepKey="voltage" t={t} />

            {/* Legacy read-only view — shown only for visits created BEFORE
                this change AND that already have typed-in voltages. New
                visits start with no voltages set → go straight to the
                photo flow below. */}
            {(visit.measurements?.voltage?.l1_l2 != null) && !visit.measurements_v2 && (
              <div data-testid="voltage-legacy-readonly"
                    className="mb-4 bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800 leading-snug">
                <b>Legacy entry.</b> This record was created before photo-only
                capture was enabled. The previously-typed values are kept
                for audit:&nbsp;
                <code className="font-mono">L1↔L2 = {visit.measurements.voltage.l1_l2}V</code>
                {visit.measurements.voltage.l2_l3 != null && <>, <code className="font-mono">L2↔L3 = {visit.measurements.voltage.l2_l3}V</code></>}
                {visit.measurements.voltage.l1_l3 != null && <>, <code className="font-mono">L1↔L3 = {visit.measurements.voltage.l1_l3}V</code></>}
              </div>
            )}

            {/* New photo-only flow — used for all NEW visits as well as
                any pre-existing visit that opts in via measurements_v2. */}
            <MeasurementsSet
              parentKind="commissioning"
              parentId={visit.id}
              phaseCount={visit.phase_count === 1 ? 1 : 3}
              nominalVoltage={visit.phase_count === 1 ? 240 : 400}
              email={visit.tech_email}
              ampsRequired={true}
              onChange={items => setMeasItems(items)} />

            <NavRow visit={visit} step={step}
              onBack={goBack}
              nextDisabled={!measComplete}
              nextDisabledReason={!measComplete ? "Capture every required photo to continue" : ""}
              onNext={async () => { await saveMeasurements({ voltage: undefined }); goNext(); }}
              t={t} />
          </Card>
        )}

        {/* Step 7: Wire diameter */}
        {step === "wire" && visit && (
          <Card data-testid="commissioning-step-wire">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.wire_step")}</h2>
            <StepHint stepKey="wire" t={t} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold mb-1">{t("commissioning.wire_compressor")}</label>
                <input data-testid="commissioning-wire-compressor"
                  value={wireC} onChange={e => setWireC(e.target.value)}
                  className="w-full border rounded px-3 py-2" placeholder={t("commissioning.wire_compressor_placeholder", "e.g. 4 AWG / 25 mm²")} />
              </div>
              {visit.is_air_center ? (
                <div className="text-sm text-slate-500 italic">{t("commissioning.wire_dryer_skipped")}</div>
              ) : (
                <div>
                  <label className="block text-sm font-bold mb-1">{t("commissioning.wire_dryer")}</label>
                  <input data-testid="commissioning-wire-dryer"
                    value={wireD} onChange={e => setWireD(e.target.value)}
                    className="w-full border rounded px-3 py-2" placeholder={t("commissioning.wire_dryer_placeholder", "e.g. 8 AWG / 10 mm² · leave empty if no dryer")} />
                </div>
              )}
            </div>
            <NavRow visit={visit} step={step}
              onBack={goBack}
              onNext={async () => { await saveMeasurements(); goNext(); }}
              t={t} />
          </Card>
        )}

        {/* Step 8: Line reactor */}
        {step === "line_reactor" && visit && (
          <Card data-testid="commissioning-step-line-reactor">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.line_reactor_step")}</h2>
            {visit.is_vsd ? (
              <>
                <p className="text-sm text-slate-600 mb-3">{t("commissioning.line_reactor_hint")}</p>
                <StepHint stepKey="line_reactor" t={t} />
                <FileUploader kind="line_reactor" accept="image/*" onUpload={(f) => uploadMedia(f, "line_reactor")} />
              </>
            ) : (
              <p className="text-sm text-slate-500 italic">{t("commissioning.line_reactor_not_vsd")}</p>
            )}
            <NavRow visit={visit} step={step} onBack={goBack} onNext={goNext} t={t} />
          </Card>
        )}

        {/* Step 9: Disclaimer + Signature */}
        {step === "disclaimer" && visit && (
          <Card data-testid="commissioning-step-disclaimer">
            <h2 className="text-xl font-bold font-['Chivo']">{t("commissioning.disclaimer_step")}</h2>
            <p className="text-sm text-slate-600 mb-3">{t("commissioning.disclaimer_intro")}</p>
            {!masterDoc && <div className="text-sm text-slate-500">{t("commissioning.loading_warranty_doc", "Loading warranty document…")}</div>}
            {masterDoc && !signedResult && (
              <>
                <div className="flex justify-end mb-2">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/warranty/master-doc.pdf?download=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="commissioning-download-policy-pdf"
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-violet-700 border border-violet-300 hover:bg-violet-700 hover:text-white px-3 py-1.5 rounded-sm bg-violet-50/40"
                    title={t("wmd.download_pdf_hint", "Download the full Warranty Policy as a PDF, including the Labor Coverage Policy section.")}
                  >
                    <DownloadSimple size={12} weight="fill" />
                    {t("wmd.download_pdf", "Download PDF")}
                  </a>
                </div>
                <DocumentRender doc={masterDoc} acceptedKeys={acceptedKeys} setAcceptedKeys={setAcceptedKeys} />
                <SignatureBox signerName={signerName} setSignerName={setSignerName}
                  method={signatureMethod} setMethod={setSignatureMethod}
                  signatureData={signatureData} setSignatureData={setSignatureData}
                  t={t} />
                <div className="mt-4 flex justify-between">
                  <button data-testid="commissioning-disclaimer-back" onClick={goBack}
                    className="px-3 py-2 text-sm">{t("commissioning.action_back")}</button>
                  <button data-testid="commissioning-disclaimer-sign" onClick={sign}
                    className="px-4 py-2 bg-violet-600 text-white rounded font-bold hover:bg-violet-700">
                    {t("commissioning.accept_cta")}
                  </button>
                </div>
              </>
            )}
            {signedResult && (
              <div data-testid="commissioning-signed-success" className="border-2 border-green-400 bg-green-50 rounded p-4 mt-4">
                <h3 className="text-lg font-bold text-green-800">{t("commissioning.signed_success")}</h3>
                <p className="text-sm mt-1">
                  {t("commissioning.signed_success_body", null, {
                    to_count: signedResult.email_recipients?.to?.length || 0,
                    cc_count: signedResult.email_recipients?.cc?.length || 0,
                  })}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a data-testid="commissioning-pdf-download"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/commissioning/${visit.id}/disclaimer.pdf`}
                    target="_blank" rel="noopener noreferrer"
                    className="px-3 py-2 bg-blue-600 text-white rounded text-sm font-bold hover:bg-blue-700">
                    {t("commissioning.download_pdf")}
                  </a>
                  <button onClick={() => navigate(backTarget)} className="px-3 py-2 border rounded text-sm">
                    {t("commissioning.done_button", "Done")}
                  </button>
                </div>
                {/* Send to ACC engineering for review */}
                <div className="mt-4 border-t pt-3">
                  {!engResult ? (
                    <>
                      <h4 className="text-sm font-bold">{t("commissioning.send_to_eng", "Send to ACC engineering for review")}</h4>
                      <p className="text-xs text-slate-600 mt-1 mb-2">
                        {t("commissioning.send_to_eng_hint", "Bundles the signed PDF + measurements + media list and emails it to ACC + USAIR engineering. They review and either approve, reject, or request changes — visible to you on this page.")}
                      </p>
                      <textarea data-testid="commissioning-eng-notes"
                        value={engNotes}
                        onChange={e => setEngNotes(e.target.value)}
                        rows={2} placeholder={t("commissioning.eng_notes_placeholder", "Notes for engineering (optional)")}
                        className="w-full border rounded px-2 py-1 text-sm" />
                      <button data-testid="commissioning-send-engineering"
                        onClick={sendToEngineering} disabled={sendingEng}
                        className="mt-2 px-3 py-2 bg-violet-600 text-white rounded text-sm font-bold hover:bg-violet-700 disabled:opacity-50">
                        {sendingEng ? "Sending…" : t("commissioning.send_eng_btn", "Send to engineering")}
                      </button>
                    </>
                  ) : (
                    <div data-testid="commissioning-eng-success"
                      className="bg-violet-50 border-l-4 border-violet-400 rounded p-3 text-sm">
                      <div className="font-bold text-violet-900">{t("commissioning.sent_to_eng", "Sent to engineering")}</div>
                      <div className="text-xs mt-1">
                        Recipients: {engResult.recipients?.join(", ")}<br />
                        Review ID: <code>{engResult.review_id}</code>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}

function ordinal(n) {
  if (n === 1) return "1st";
  if (n === 2) return "2nd";
  if (n === 3) return "3rd";
  return `${n}th`;
}

function Pill({ children, tone = "slate", "data-testid": tid }) {
  const map = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    violet: "bg-violet-100 text-violet-700 border-violet-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    blue: "bg-blue-100 text-blue-700 border-blue-300",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
    red: "bg-red-100 text-red-800 border-red-300",
    orange: "bg-orange-100 text-orange-800 border-orange-300",
  };
  return <span data-testid={tid} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${map[tone]}`}>{children}</span>;
}

/**
 * Inline "what to capture" hint banner — appears under each wizard step
 * heading. Tells the tech exactly what the photo / video must include
 * (legibility, framing, what NOT to capture). Localized via i18n.
 */
function StepHint({ stepKey, t }) {
  const key = `commissioning.hint_${stepKey}`;
  const text = t(key);
  if (!text || text === key) return null;
  return (
    <div data-testid={`commissioning-hint-${stepKey}`}
      className="mb-3 border-l-4 border-blue-400 bg-blue-50 px-3 py-2 rounded text-xs text-slate-800">
      <span className="font-bold text-blue-900 uppercase tracking-wider text-[10px] block mb-0.5">
        {t("commissioning.hint_label")}
      </span>
      {text}
    </div>
  );
}

function Card({ children, "data-testid": tid }) {
  return <div data-testid={tid} className="bg-white border rounded-lg p-5 shadow-sm">{children}</div>;
}

function Stepper({ steps, current, skipLineReactor }) {
  const visible = steps.filter(s => !(s === "line_reactor" && skipLineReactor));
  const idx = visible.indexOf(current);
  const pct = idx >= 0 ? Math.round(((idx + 1) / visible.length) * 100) : 0;
  return (
    <div className="mb-4" data-testid="commissioning-stepper">
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-violet-600 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-slate-500 mt-1 font-bold uppercase tracking-wider">
        Step {idx + 1} of {visible.length}
      </div>
    </div>
  );
}

function NavRow({ onBack, onNext, t, skipLabel, nextDisabled, nextDisabledReason }) {
  return (
    <div className="mt-5 flex justify-between">
      <button data-testid="commissioning-step-back" onClick={onBack}
        className="px-3 py-2 text-sm border rounded">{t("commissioning.action_back")}</button>
      <button data-testid="commissioning-step-next" onClick={onNext}
        disabled={!!nextDisabled}
        title={nextDisabledReason || ""}
        className="px-4 py-2 bg-violet-600 text-white rounded font-bold text-sm hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
        {skipLabel ? t("commissioning.action_skip") : t("commissioning.action_save")}
      </button>
    </div>
  );
}

function FileUploader({ kind, label, accept, multiple, onUpload }) {
  const [camOpen, setCamOpen] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const isVideo = (accept || "").includes("video");
  const isPhoto = (accept || "").includes("image");
  const allowsBoth = isVideo && isPhoto;
  const [pickedMode, setPickedMode] = useState(isVideo ? "video" : "photo");
  return (
    <div className="border-2 border-dashed border-slate-300 rounded p-4 text-center bg-slate-50 hover:bg-slate-100 transition">
      {/* iter137.5 — Live capture only. No file uploads. */}
      <div className="text-[11px] text-slate-500 mb-2 font-semibold uppercase tracking-wider">
        Live capture only
      </div>
      {allowsBoth ? (
        <div className="flex gap-1.5 justify-center">
          <button
            data-testid={`commissioning-capture-photo-${kind}`}
            onClick={() => { setPickedMode("photo"); setCamOpen(true); }}
            className="px-3 py-1.5 bg-violet-600 text-white text-xs font-bold rounded hover:bg-violet-700 inline-flex items-center gap-1.5"
          >
            📷 Photo
          </button>
          <button
            data-testid={`commissioning-capture-video-${kind}`}
            onClick={() => { setPickedMode("video"); setCamOpen(true); }}
            className="px-3 py-1.5 bg-rose-600 text-white text-xs font-bold rounded hover:bg-rose-700 inline-flex items-center gap-1.5"
          >
            🎥 Video
          </button>
        </div>
      ) : (
        <button
          data-testid={`commissioning-upload-${kind}`}
          onClick={() => setCamOpen(true)}
          className="text-sm font-bold text-violet-700"
        >
          {label || (isVideo ? "Capture video" : "Capture picture")}
        </button>
      )}
      <LiveCameraCapture
        open={camOpen}
        mode={pickedMode}
        maxSeconds={120}
        onClose={() => setCamOpen(false)}
        onCapture={async (blob, info) => {
          const f = new File([blob], info.filename, { type: info.mime_type });
          const r = await onUpload(f, uploaded.length);
          if (r) setUploaded((u) => [...u, r.filename || f.name]);
        }}
        testIdPrefix={`commissioning-livecam-${kind}`}
      />
      {uploaded.length > 0 && (
        <ul className="mt-2 text-xs text-slate-600">
          {uploaded.map((f, i) => <li key={i}>✓ {f}</li>)}
        </ul>
      )}
    </div>
  );
}

function VoltageInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm font-bold mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <input
          data-testid={testid}
          type="number" step="0.1"
          value={value} onChange={e => onChange(e.target.value)}
          className="w-full border rounded px-2 py-1 text-right"
          placeholder="V"
        />
        <span className="text-sm text-slate-500">V</span>
      </div>
    </div>
  );
}

function DocumentRender({ doc, acceptedKeys, setAcceptedKeys }) {
  const t = useT();
  const sigKeys = useMemo(() => new Set((doc.signature_clauses || []).map(c => c.key)), [doc]);
  const grouped = useMemo(() => groupMasterDocSections(doc.sections || []), [doc.sections]);
  const counts = useMemo(() => policyGroupCounts(doc.sections || []), [doc.sections]);
  const laborCount = counts.labor_coverage || 0;
  return (
    <div data-testid="commissioning-doc-render" className="border rounded max-h-[420px] overflow-y-auto p-4 bg-slate-50 text-sm space-y-4">
      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-2 flex-wrap">
        <span>{doc.version} · {doc.role} view · {doc.sections.length} sections</span>
        {laborCount > 0 && (
          <span
            className="inline-flex items-center gap-1 bg-violet-100 text-violet-800 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
            data-testid="commissioning-labor-coverage-badge"
            title={t("wmd.labor_coverage_policy_summary", "When warranty labor is covered.")}
          >
            <Wrench size={11} weight="fill" />
            {t("wmd.labor_coverage_policy", "Labor Coverage Policy")} · {laborCount}
          </span>
        )}
      </div>
      {grouped.map((block, idx) => {
        if (block.kind === "section") {
          const s = block.section;
          return (
            <DocSectionAccept
              key={`s-${idx}-${s.key}`}
              section={s} sigKeys={sigKeys}
              acceptedKeys={acceptedKeys} setAcceptedKeys={setAcceptedKeys}
              t={t}
            />
          );
        }
        // policy group
        return (
          <div
            key={`g-${idx}-${block.group.id}`}
            className="border-l-4 border-violet-300 bg-violet-50/50 rounded-sm pl-3 pr-2 py-3"
            data-testid={`commissioning-policy-group-${block.group.id}`}
          >
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Wrench size={14} weight="fill" className="text-violet-700" />
              <h3 className="text-sm font-black uppercase tracking-wider text-violet-900">
                {t(block.group.titleKey, block.group.titleFallback)}
              </h3>
              <span className="text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">
                {block.sections.length} {t("wmd.sections_short", "sections")}
              </span>
            </div>
            <p className="text-xs italic text-slate-600 mb-2 leading-relaxed">
              {t(block.group.summaryKey, block.group.summaryFallback)}
            </p>
            <div className="space-y-3 pl-2 border-l border-violet-200">
              {block.sections.map((s) => (
                <DocSectionAccept
                  key={s.key}
                  section={s} sigKeys={sigKeys}
                  acceptedKeys={acceptedKeys} setAcceptedKeys={setAcceptedKeys}
                  t={t}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DocSectionAccept({ section: s, sigKeys, acceptedKeys, setAcceptedKeys, t }) {
  return (
    <div>
      <h3 className="font-bold text-slate-900">{s.title}</h3>
      <div className="text-xs text-slate-700 whitespace-pre-wrap mt-1">{s.body_md}</div>
      {sigKeys.has(s.key) && (
        <label className="mt-2 flex items-center gap-2 text-xs font-bold text-violet-700">
          <input type="checkbox"
            data-testid={`commissioning-clause-${s.key}`}
            checked={!!acceptedKeys[s.key]}
            onChange={e => setAcceptedKeys({ ...acceptedKeys, [s.key]: e.target.checked })}
          />
          {t("commissioning.accept_clause", "I accept this clause")}
        </label>
      )}
    </div>
  );
}

function SignatureBox({ signerName, setSignerName, method, setMethod, signatureData, setSignatureData, t }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  const beginStroke = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const moveStroke = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const endStroke = () => {
    if (!drawing) return;
    setDrawing(false);
    setSignatureData(canvasRef.current.toDataURL("image/png"));
  };
  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setSignatureData("");
  };

  return (
    <div className="mt-4 border rounded p-4 bg-white">
      <label className="block text-sm font-bold mb-1">{t("commissioning.signature_method")}</label>
      <div className="flex gap-2 mb-3">
        <button data-testid="commissioning-method-typed"
          onClick={() => { setMethod("typed"); setSignatureData(""); }}
          className={`px-3 py-1 text-sm rounded border ${method === "typed" ? "bg-violet-600 text-white border-violet-600" : "bg-white"}`}>
          {t("commissioning.method_typed")}
        </button>
        <button data-testid="commissioning-method-drawn"
          onClick={() => setMethod("drawn")}
          className={`px-3 py-1 text-sm rounded border ${method === "drawn" ? "bg-violet-600 text-white border-violet-600" : "bg-white"}`}>
          {t("commissioning.method_drawn")}
        </button>
      </div>
      <label className="block text-sm font-bold mb-1">{t("commissioning.full_legal_name", "Full legal name")}</label>
      <input data-testid="commissioning-signer-name"
        value={signerName} onChange={e => setSignerName(e.target.value)}
        className="w-full border rounded px-3 py-2 mb-3" />
      {method === "typed" && (
        <div className="text-2xl italic font-bold text-slate-800 border-b-2 border-slate-400 py-2 inline-block">
          {signerName || "—"}
        </div>
      )}
      {method === "drawn" && (
        <div>
          <canvas
            data-testid="commissioning-signature-canvas"
            ref={canvasRef}
            width={500} height={140}
            className="border rounded bg-slate-50 w-full max-w-md cursor-crosshair touch-none"
            onMouseDown={beginStroke} onMouseMove={moveStroke} onMouseUp={endStroke} onMouseLeave={endStroke}
            onTouchStart={beginStroke} onTouchMove={moveStroke} onTouchEnd={endStroke}
          />
          <button onClick={clearCanvas} className="mt-1 text-xs text-slate-500 hover:text-slate-700">{t("commissioning.clear_signature", "Clear")}</button>
        </div>
      )}
    </div>
  );
}
